import React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

import { ReactComponent as ArrowDownIcon } from "../../../assets/svg/arrow_down.svg";
import { ReactComponent as LessonCompleteIcon } from "../../../assets/svg/lesson_complete.svg";
import { ReactComponent as StudentProfileIcon } from "../../../assets/svg/student_profile.svg";
import { ReactComponent as CautionIcon } from "../../../assets/svg/caution.svg";
import ReissueStudentPasswordModal from "../../../organisms/teacher/ReissueStudentPasswordModal";
import Sidebar from "../../../organisms/teacher/Sidebar";
import BaWorkSection from "../../../organisms/BaWorkSection";
import LessonAccordion from "../../../organisms/LessonAccordion";
import Spinner from "../../../atoms/Spinner";
import {
  ITeachingMaterial,
  ILessonByTeachingMaterial,
  IPortfolio,
  IStudent,
  IStudentActionListMaterial,
} from "../../../state";
import {
  CATEGORY_ORDER,
  CategoryType,
} from "../../../constants/teaching_materials";
import useCategorizeMaterials from "../../../hooks/useCategorizeMaterials";
import {
  getCurrentMaterials,
  resolveActiveTab,
} from "../../../libs/teachingMaterialUtils";
import { generateFullName, getPasswordDisplay } from "../../../libs/Util";
import useOnClickOutside from "../../../hooks/useOnClickOutside";

import IssueList from "./IssueList";
import styles from "./ClassDataContents.module.scss";

export type Props = {
  calling: boolean;
  materialCalling: boolean;
  student?: IStudent;
  students: IStudent[];
  classId: number;
  teaching_materials: ITeachingMaterial[];
  lessonsByTeachingMaterial?: ILessonByTeachingMaterial[];
  portfolio: IPortfolio | null;
  teachingMaterialId: number;
  studentActionList: IStudentActionListMaterial | null;
  currentTeacherId: number;
  onBack: () => void;
  handleComment: (
    content: string,
    answerId: number,
    comment_id?: number,
  ) => void;
  handleDeleteComment: (answer_id: number, comment_id: number) => void;
  handleToggleComment: (
    answer_id: number,
    comment_id: number,
    is_hidden: boolean,
  ) => void;
  onTeachingMaterialChange: (material_id: number) => void;
  onChangeStudent: (student: IStudent) => void;
  onReissueStudentPassword: () => void;
};

type SortOrder = "kana" | "student_no";

const ClassDataContents: React.FC<Props> = ({
  calling,
  materialCalling,
  student,
  students,
  classId,
  teaching_materials,
  lessonsByTeachingMaterial,
  portfolio,
  teachingMaterialId,
  studentActionList,
  currentTeacherId,
  onBack,
  onTeachingMaterialChange,
  onChangeStudent,
  onReissueStudentPassword,
  handleComment,
  handleDeleteComment,
  handleToggleComment,
}) => {
  const hasLoaded = !calling && lessonsByTeachingMaterial !== undefined;

  const [sortOrder, setSortOrder] = React.useState<SortOrder>("kana");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const headerRowRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(headerRowRef, () => setIsDropdownOpen(false));

  const categorizedMaterials = useCategorizeMaterials(teaching_materials);

  const [manualTab, setManualTab] = React.useState<CategoryType | null>(null);

  const activeTab = React.useMemo(() => {
    if (manualTab !== null && categorizedMaterials[manualTab].length > 0) {
      return manualTab;
    }
    const autoTab = CATEGORY_ORDER.find(cat =>
      categorizedMaterials[cat].some(m => m.id === teachingMaterialId),
    );
    if (autoTab) return autoTab;
    return resolveActiveTab(categorizedMaterials, null);
  }, [manualTab, categorizedMaterials, teachingMaterialId]);

  const currentMaterials = React.useMemo(
    () => getCurrentMaterials(categorizedMaterials, activeTab),
    [categorizedMaterials, activeTab],
  );

  React.useEffect(() => {
    if (currentMaterials.length === 0) return;
    const isCurrentInTab = currentMaterials.some(
      m => m.id === teachingMaterialId,
    );
    if (!isCurrentInTab) {
      onTeachingMaterialChange(currentMaterials[0].id);
    }
  }, [teachingMaterialId, currentMaterials, onTeachingMaterialChange]);

  const [
    isShowModalReissuePassword,
    setShowModalReissuePassword,
  ] = React.useState(false);

  const sortedStudents = React.useMemo(() => {
    const collator = new Intl.Collator(["ja", "en"], { numeric: true });
    return [...students].sort((a, b) => {
      if (sortOrder === "student_no") {
        if (!a.student_no_in_school) return 1;
        if (!b.student_no_in_school) return -1;
        return collator.compare(a.student_no_in_school, b.student_no_in_school);
      }
      const aKana = `${a.last_name_kana}${a.first_name_kana}`;
      const bKana = `${b.last_name_kana}${b.first_name_kana}`;
      return aKana.localeCompare(bKana, "ja");
    });
  }, [students, sortOrder]);

  const studentName = student
    ? generateFullName([student.last_name, student.first_name], "")
    : "";

  const studentNameKana = student
    ? `${student.last_name_kana}${student.first_name_kana}`
    : "";

  const currentActionList = studentActionList?.lessons ?? [];

  const currentMaterial = React.useMemo(
    () => currentMaterials.find(m => m.id === teachingMaterialId),
    [currentMaterials, teachingMaterialId],
  );

  const isMiddleOrHigh = React.useMemo(
    () =>
      categorizedMaterials.middle.some(m => m.id === teachingMaterialId) ||
      categorizedMaterials.high.some(m => m.id === teachingMaterialId),
    [categorizedMaterials, teachingMaterialId],
  );

  return (
    <>
      {teaching_materials.length === 0 || materialCalling ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
        </div>
      ) : (
        <div className={styles.pageLayout}>
          <Sidebar
            classList={[]}
            currentMaterials={currentMaterials}
            viewingMaterialId={teachingMaterialId || null}
            activeModalId={null}
            activeTab={activeTab}
            targetClassIds={[classId]}
            showTabs={categorizedMaterials.hasMultipleMaterialCategories}
            hasMiddle={categorizedMaterials.hasMiddle}
            hasHigh={categorizedMaterials.hasHigh}
            hasCore={categorizedMaterials.hasCore}
            hasBase={categorizedMaterials.hasBase}
            backLabel="データ選択に戻る"
            onBackToClass={onBack}
            onTabClick={tab => setManualTab(tab)}
            onItemClick={(_e, materialId) =>
              onTeachingMaterialChange(materialId)
            }
          />

          <div className={styles.main}>
            {!hasLoaded ? (
              <div className={styles.mainLoading}>
                <Spinner spinColor="black" />
              </div>
            ) : (
              <div className={styles.innerContent}>
                {student && (
                  <>
                    <div className={styles.studentHeaderRow} ref={headerRowRef}>
                      <div
                        className={clsx(
                          styles.studentSelectorWrapper,
                          isDropdownOpen && styles.open,
                        )}
                      >
                        <div
                          className={styles.studentSelectorBox}
                          onClick={() => setIsDropdownOpen(prev => !prev)}
                        >
                          <div className={styles.studentAvatar}>
                            {student.photo?.url ? (
                              <img src={student.photo.url} alt={studentName} />
                            ) : (
                              <StudentProfileIcon />
                            )}
                          </div>
                          <div className={styles.studentTextSection}>
                            <div className={styles.nameKanaRow}>
                              <span className={styles.studentName}>
                                {studentName}
                              </span>
                              <span className={styles.studentKana}>
                                {studentNameKana}
                              </span>
                            </div>
                            <div className={styles.studentMeta}>
                              <span className={styles.studentNo}>
                                学籍番号：{student.student_no_in_school}
                              </span>
                              <span>ログインID：{student.identity}</span>
                              <span>
                                {getPasswordDisplay(student.temporary_password)}
                              </span>
                            </div>
                          </div>
                          <div className={styles.dropdownArrowWrapper}>
                            <ArrowDownIcon className={styles.dropdownArrow} />
                          </div>
                        </div>
                        {isDropdownOpen && (
                          <div className={styles.studentDropdown}>
                            {sortedStudents.map((s, idx) => {
                              const sName = generateFullName(
                                [s.last_name, s.first_name],
                                "",
                              );
                              const sKana = `${s.last_name_kana}${s.first_name_kana}`;
                              const isSelected = s.id === student.id;
                              return (
                                <React.Fragment key={s.id}>
                                  {idx > 0 && (
                                    <div className={styles.dropdownDivider} />
                                  )}
                                  <div
                                    className={clsx(
                                      styles.dropdownItem,
                                      isSelected && styles.dropdownItemSelected,
                                    )}
                                    onClick={() => {
                                      onChangeStudent(s);
                                      setIsDropdownOpen(false);
                                    }}
                                  >
                                    <div className={styles.studentAvatar}>
                                      {s.photo?.url ? (
                                        <img src={s.photo.url} alt={sName} />
                                      ) : (
                                        <StudentProfileIcon />
                                      )}
                                    </div>
                                    <div className={styles.dropdownTextSection}>
                                      <div
                                        className={styles.dropdownNameKanaRow}
                                      >
                                        <span className={styles.dropdownName}>
                                          {sName}
                                        </span>
                                        <span className={styles.dropdownKana}>
                                          {sKana}
                                        </span>
                                      </div>
                                      <div className={styles.dropdownMeta}>
                                        <span className={styles.studentNo}>
                                          学籍番号：{s.student_no_in_school}
                                        </span>
                                        <span>ログインID：{s.identity}</span>
                                        <span>
                                          {getPasswordDisplay(
                                            s.temporary_password,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className={styles.dropdownCheckWrapper}
                                    >
                                      {isSelected && (
                                        <LessonCompleteIcon
                                          className={styles.dropdownCheck}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div
                        className={clsx(
                          styles.sortOptions,
                          isDropdownOpen && styles.open,
                        )}
                      >
                        <div
                          className={clsx(
                            styles.sortItem,
                            sortOrder === "kana" && styles.sortActive,
                          )}
                          onClick={() => setSortOrder("kana")}
                        >
                          <div className={styles.radioCircle} />
                          <span>50音順</span>
                        </div>
                        <div
                          className={clsx(
                            styles.sortItem,
                            sortOrder === "student_no" && styles.sortActive,
                          )}
                          onClick={() => setSortOrder("student_no")}
                        >
                          <div className={styles.radioCircle} />
                          <span>学籍番号順</span>
                        </div>
                      </div>
                    </div>
                    {isDropdownOpen &&
                      createPortal(
                        <div
                          className={styles.backdrop}
                          onClick={() => setIsDropdownOpen(false)}
                        />,
                        document.body,
                      )}
                    <div className={styles.passwordReissue}>
                      <button
                        className={styles.passwordReissueBtn}
                        disabled={calling}
                        onClick={() => setShowModalReissuePassword(true)}
                      >
                        <CautionIcon className={styles.cautionIcon} />
                        パスワード再発行
                      </button>
                    </div>

                    <div className={styles.contentArea}>
                      {currentMaterial && (
                        <div className={styles.materialHeader}>
                          <span className={styles.materialVol}>
                            {currentMaterial.name}
                          </span>
                          <span className={styles.materialTitle}>
                            {currentMaterial.title.replace(
                              /\s*-高校-|\s*-中学-/g,
                              "",
                            )}
                          </span>
                        </div>
                      )}
                      <BaWorkSection
                        key={teachingMaterialId}
                        oppas={
                          portfolio?.one_page_portfolio_assessments ?? null
                        }
                      />
                      {lessonsByTeachingMaterial
                        ?.filter(lesson => !lesson.title?.includes("はじめに"))
                        .map(lesson => {
                          const isLeaf = lesson.title.includes(
                            "実践ワークシート",
                          );
                          return (
                            <LessonAccordion
                              key={`${lesson.id}-${teachingMaterialId}`}
                              lesson={lesson}
                              isLeaf={isLeaf}
                              actionLesson={currentActionList.find(
                                l => l.id === lesson.id,
                              )}
                            >
                              <IssueList
                                issues={lesson.issues}
                                isLeaf={isLeaf}
                                isMiddleOrHigh={isMiddleOrHigh}
                                calling={calling}
                                currentTeacherId={currentTeacherId}
                                handleComment={handleComment}
                                handleDeleteComment={handleDeleteComment}
                                handleToggleComment={handleToggleComment}
                              />
                            </LessonAccordion>
                          );
                        })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {isShowModalReissuePassword && student && (
        <ReissueStudentPasswordModal
          student_name={studentName}
          onClose={() => setShowModalReissuePassword(false)}
          onReissueStudentPassword={onReissueStudentPassword}
        />
      )}
    </>
  );
};

export default ClassDataContents;
